import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, SIZES, fmt, formatApiError } from "@/lib/api";
import { UserPlus, X } from "lucide-react";
import LineItemsSection from "@/components/LineItemsSection";

const todayStr = () => new Date().toISOString().slice(0, 10);
const blankItem = () => ({ product_id: "", style_id: "", product_name: "", color: "", sizes: Object.fromEntries(SIZES.map((s) => [s, 0])), unit_price: 0 });

export default function InvoiceCreate() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showNewCust, setShowNewCust] = useState(false);
  const [inv, setInv] = useState({
    customer_id: "", invoice_date: todayStr(), shipping_address: "", gst_number: "",
    shipping_charges: 0, discount: 0,
    cgst_pct: 0, sgst_pct: 0, igst_pct: 0,
    payment_method: "", amount_received: 0,
    items: [blankItem()],
    return_items: [],
    return_cgst_pct: 0, return_sgst_pct: 0, return_igst_pct: 0,
    is_defect: false,
    defect_note: "",
  });

  const loadCustomers = () => api.get("/customers").then(({ data }) => setCustomers(data));
  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data));
  useEffect(() => {
    loadProducts();
    loadCustomers();
  }, []);

  const selectedCustomer = customers.find((c) => c.id === inv.customer_id);
  useEffect(() => {
    if (selectedCustomer) {
      setInv((p) => ({ ...p,
        shipping_address: p.shipping_address || selectedCustomer.address || "",
        gst_number: p.gst_number || selectedCustomer.gst_number || "",
      }));
    }
  }, [inv.customer_id, selectedCustomer]);

  const makeItemHandlers = (field) => ({
    updateItem: (i, patch) => setInv((p) => ({ ...p, [field]: p[field].map((it, ix) => ix === i ? { ...it, ...patch } : it) })),
    updateSize: (i, s, v) => setInv((p) => ({ ...p, [field]: p[field].map((it, ix) => ix === i ? { ...it, sizes: { ...it.sizes, [s]: Math.max(0, Number(v) || 0) } } : it) })),
    addItem: () => setInv((p) => ({ ...p, [field]: [...p[field], blankItem()] })),
    removeItem: (i) => setInv((p) => ({ ...p, [field]: p[field].filter((_, ix) => ix !== i) })),
    onProductChange: (i, productId) => {
      const prod = products.find((x) => x.id === productId);
      setInv((p) => ({
        ...p,
        [field]: p[field].map((it, ix) => ix !== i ? it : (!prod
          ? { ...it, product_id: "", style_id: "", product_name: "", color: "", unit_price: 0 }
          : { ...it, product_id: prod.id, style_id: prod.style_id, product_name: prod.name, color: prod.colors[0]?.color || "", unit_price: prod.selling_price })),
      }));
    },
  });
  const itemHandlers = makeItemHandlers("items");
  const returnHandlers = makeItemHandlers("return_items");

  const stockFor = (productId, color, size) => {
    const p = products.find((x) => x.id === productId);
    const c = p?.colors.find((c) => c.color === color);
    return c?.sizes[size] ?? 0;
  };

  const linesFor = (items) => {
    let sub = 0;
    const lines = items.map((it) => {
      const qty = SIZES.reduce((a, s) => a + (it.sizes[s] || 0), 0);
      const lt = qty * (Number(it.unit_price) || 0);
      sub += lt; return { qty, lt };
    });
    return { sub, lines, totalQty: lines.reduce((a, l) => a + l.qty, 0) };
  };

  const totals = useMemo(() => {
    const { sub, lines, totalQty } = linesFor(inv.items);
    const taxable = Math.max(0, sub - Number(inv.discount || 0));
    const cgst = taxable * Number(inv.cgst_pct || 0) / 100;
    const sgst = taxable * Number(inv.sgst_pct || 0) / 100;
    const igst = taxable * Number(inv.igst_pct || 0) / 100;
    const saleTotal = taxable + cgst + sgst + igst + Number(inv.shipping_charges || 0);

    const { sub: returnSub, lines: returnLines, totalQty: returnTotalQty } = linesFor(inv.return_items);
    const returnCgst = returnSub * Number(inv.return_cgst_pct || 0) / 100;
    const returnSgst = returnSub * Number(inv.return_sgst_pct || 0) / 100;
    const returnIgst = returnSub * Number(inv.return_igst_pct || 0) / 100;
    const returnTotal = returnSub + returnCgst + returnSgst + returnIgst;

    const grand = saleTotal - returnTotal;
    const balance = Math.max(0, grand - Number(inv.amount_received || 0));
    return {
      sub, taxable, cgst, sgst, igst, saleTotal, lines, totalQty,
      returnSub, returnCgst, returnSgst, returnIgst, returnTotal, returnLines, returnTotalQty,
      grand, balance,
    };
  }, [inv]);

  // Validation flags: qty overstock per cell (sale items only — returns add stock back, no cap)
  const overstockMap = useMemo(() => {
    const map = {};
    inv.items.forEach((it, i) => {
      if (!it.product_id || !it.color) return;
      SIZES.forEach((s) => {
        const v = it.sizes[s] || 0;
        const stock = stockFor(it.product_id, it.color, s);
        if (v > stock) map[`${i}-${s}`] = { qty: v, stock };
      });
    });
    return map;
  }, [inv.items, products]);

  const hasOverstock = Object.keys(overstockMap).length > 0;
  const hasReturnItems = inv.return_items.some((it) => it.product_id && it.color && SIZES.some((s) => (it.sizes[s] || 0) > 0));

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        ...inv,
        shipping_charges: Number(inv.shipping_charges) || 0,
        discount: Number(inv.discount) || 0,
        cgst_pct: Number(inv.cgst_pct) || 0,
        sgst_pct: Number(inv.sgst_pct) || 0,
        igst_pct: Number(inv.igst_pct) || 0,
        return_items: inv.return_items.filter((it) => it.product_id),
        return_cgst_pct: Number(inv.return_cgst_pct) || 0,
        return_sgst_pct: Number(inv.return_sgst_pct) || 0,
        return_igst_pct: Number(inv.return_igst_pct) || 0,
        amount_received: Number(inv.amount_received) || 0,
        payment_method: inv.payment_method || "",
        is_defect: !!inv.is_defect,
        defect_note: (inv.defect_note || "").trim(),
      };
      const { data } = await api.post("/invoices", payload);
      nav(`/invoices/${data.id}`);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const returnItemsValid = inv.return_items.every((it) => !it.product_id || (it.color && SIZES.some((s) => (it.sizes[s] || 0) > 0)));
  const canSubmit = !hasOverstock && returnItemsValid && totals.grand >= 0 && inv.customer_id && inv.items.length > 0 && inv.items.every((it) => it.product_id && it.color && SIZES.some((s) => (it.sizes[s] || 0) > 0));

  const status = (() => {
    const amt = Number(inv.amount_received || 0);
    if (amt <= 0 || !inv.payment_method) return "DRAFT";
    if (amt + 0.01 >= totals.grand) return "CLEARED";
    return "BALANCE_PENDING";
  })();
  const statusCls = status === "CLEARED" ? "badge-cleared" : status === "BALANCE_PENDING" ? "badge-pending" : "badge-draft";

  return (
    <div data-testid="invoice-create-page">
      <div className="page-header">
        <div><h1 className="page-title">New Invoice</h1><p className="page-sub">Create and generate a tax invoice</p></div>
        <span className={`badge badge-lg ${statusCls}`} data-testid="preview-status">{status.replace("_", " ")}</span>
      </div>

      {err && <div className="error-msg" data-testid="invoice-error" style={{ marginBottom: 14 }}>{err}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="grid-form">
          <div className="field"><label>Invoice Date</label><input data-testid="inv-date" className="input" type="date" value={inv.invoice_date} onChange={(e) => setInv({ ...inv, invoice_date: e.target.value })}/></div>
          <div className="field"><label>Customer *</label>
            <div style={{ display: "flex", gap: 6 }}>
              <select data-testid="inv-customer" className="select" value={inv.customer_id} onChange={(e) => setInv({ ...inv, customer_id: e.target.value, shipping_address: "", gst_number: "" })} style={{ flex: 1 }}>
                <option value="">Select customer...</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.phone ? `— ${c.phone}` : ""}</option>)}
              </select>
              <button type="button" className="btn btn-secondary btn-sm" data-testid="add-new-customer-btn" onClick={() => setShowNewCust(true)} title="Add new customer"><UserPlus size={14}/></button>
            </div>
          </div>
          <div className="field"><label>GST Number</label><input data-testid="inv-gst" className="input" value={inv.gst_number} onChange={(e) => setInv({ ...inv, gst_number: e.target.value })}/></div>
        </div>
        <div className="field" style={{ marginTop: 14 }}><label>Shipping Address</label><textarea data-testid="inv-ship-addr" className="input" rows="2" value={inv.shipping_address} onChange={(e) => setInv({ ...inv, shipping_address: e.target.value })}/></div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontWeight: 500 }}>
                <input
                  type="checkbox"
                  data-testid="inv-is-defect"
                  checked={inv.is_defect}
                  onChange={(e) => setInv({ ...inv, is_defect: e.target.checked, payment_method: e.target.checked ? "" : inv.payment_method, amount_received: e.target.checked ? 0 : inv.amount_received })}
                />
                This is a defect / damage stock adjustment (not a sale)
              </label>
              {inv.is_defect && (
                <div className="field" style={{ marginTop: 12 }}>
                  <label>Defect Note (optional)</label>
                  <textarea
                    data-testid="inv-defect-note"
                    className="input"
                    rows="2"
                    placeholder="e.g. torn fabric on size 40, stitching defect on 2 pieces"
                    value={inv.defect_note}
                    onChange={(e) => setInv({ ...inv, defect_note: e.target.value })}
                  />
                </div>
              )}
            </div>

      <LineItemsSection
        title="Line Items"
        testIdPrefix="line"
        items={inv.items}
        lines={totals.lines}
        products={products}
        stockFor={stockFor}
        overstockMap={overstockMap}
        onProductChange={itemHandlers.onProductChange}
        onUpdateItem={itemHandlers.updateItem}
        onUpdateSize={itemHandlers.updateSize}
        onAddItem={itemHandlers.addItem}
        onRemoveItem={itemHandlers.removeItem}
        addLabel="Add Item"
        onStockAdjusted={loadProducts}
      />

      <LineItemsSection
        title="Return Items"
        testIdPrefix="return-line"
        items={inv.return_items}
        lines={totals.returnLines}
        products={products}
        stockFor={stockFor}
        onProductChange={returnHandlers.onProductChange}
        onUpdateItem={returnHandlers.updateItem}
        onUpdateSize={returnHandlers.updateSize}
        onAddItem={returnHandlers.addItem}
        onRemoveItem={returnHandlers.removeItem}
        addLabel="Add Return Item"
        variant="return"
        helperText="Items the customer is returning as part of this sale. Their amount is deducted from the invoice total, and stock is added back automatically."
      />

      {hasReturnItems && (
        <div className="card" style={{ marginBottom: 16, border: "1px solid #FCA5A5" }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--danger, #DC2626)", marginBottom: 10 }}>Return Items GST (optional)</h2>
          <div className="grid-3">
            <div className="field"><label>Return CGST %</label><input data-testid="inv-return-cgst" className="input num" type="number" step="0.01" min="0" value={inv.return_cgst_pct} onChange={(e) => setInv({ ...inv, return_cgst_pct: e.target.value })}/></div>
            <div className="field"><label>Return SGST %</label><input data-testid="inv-return-sgst" className="input num" type="number" step="0.01" min="0" value={inv.return_sgst_pct} onChange={(e) => setInv({ ...inv, return_sgst_pct: e.target.value })}/></div>
            <div className="field"><label>Return IGST %</label><input data-testid="inv-return-igst" className="input num" type="number" step="0.01" min="0" value={inv.return_igst_pct} onChange={(e) => setInv({ ...inv, return_igst_pct: e.target.value })}/></div>
          </div>
        </div>
      )}

      {/* Taxes + Charges + Totals */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 24, alignItems: "start" }}>
          <div>
            <div className="grid-3" style={{ marginBottom: 12 }}>
              <div className="field"><label>CGST %</label><input data-testid="inv-cgst" className="input num" type="number" step="0.01" min="0" value={inv.cgst_pct} onChange={(e) => setInv({ ...inv, cgst_pct: e.target.value })}/></div>
              <div className="field"><label>SGST %</label><input data-testid="inv-sgst" className="input num" type="number" step="0.01" min="0" value={inv.sgst_pct} onChange={(e) => setInv({ ...inv, sgst_pct: e.target.value })}/></div>
              <div className="field"><label>IGST %</label><input data-testid="inv-igst" className="input num" type="number" step="0.01" min="0" value={inv.igst_pct} onChange={(e) => setInv({ ...inv, igst_pct: e.target.value })}/></div>
            </div>
            <div className="grid-2">
              <div className="field"><label>Shipping Charges (₹)</label><input data-testid="inv-shipping" className="input num" type="number" step="0.01" value={inv.shipping_charges} onChange={(e) => setInv({ ...inv, shipping_charges: e.target.value })}/></div>
              <div className="field"><label>Discount (₹)</label><input data-testid="inv-discount" className="input num" type="number" step="0.01" value={inv.discount} onChange={(e) => setInv({ ...inv, discount: e.target.value })}/></div>
            </div>
          </div>
          <div style={{ background: "#F8FAFC", border: "1px solid var(--border)", borderRadius: 4, padding: 16 }}>
            <Row label="Total Qty" value={<span data-testid="total-qty" className="tabular">{totals.totalQty}</span>}/>
            <Row label="Subtotal" value={`₹ ${fmt(totals.sub)}`}/>
            <Row label="Discount" value={`- ₹ ${fmt(inv.discount)}`}/>
            {totals.cgst > 0 && <Row label={`CGST (${inv.cgst_pct}%)`} value={`+ ₹ ${fmt(totals.cgst)}`}/>}
            {totals.sgst > 0 && <Row label={`SGST (${inv.sgst_pct}%)`} value={`+ ₹ ${fmt(totals.sgst)}`}/>}
            {totals.igst > 0 && <Row label={`IGST (${inv.igst_pct}%)`} value={`+ ₹ ${fmt(totals.igst)}`}/>}
            <Row label="Shipping" value={`+ ₹ ${fmt(inv.shipping_charges)}`}/>
            {hasReturnItems && (
              <div style={{ borderTop: "1px dashed var(--border)", marginTop: 10, paddingTop: 10 }}>
                <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Sale Total</span>} value={`₹ ${fmt(totals.saleTotal)}`}/>
                <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Return Qty</span>} value={<span className="tabular" style={{ color: "var(--danger, #DC2626)" }}>{totals.returnTotalQty}</span>}/>
                <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>Return Subtotal</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnSub)}</span>}/>
                {totals.returnCgst > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return CGST (${inv.return_cgst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnCgst)}</span>}/>}
                {totals.returnSgst > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return SGST (${inv.return_sgst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnSgst)}</span>}/>}
                {totals.returnIgst > 0 && <Row label={<span style={{ color: "var(--danger, #DC2626)" }}>{`Return IGST (${inv.return_igst_pct}%)`}</span>} value={<span style={{ color: "var(--danger, #DC2626)" }}>− ₹ {fmt(totals.returnIgst)}</span>}/>}
              </div>
            )}
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 10 }}>
              <Row label={<strong>Grand Total</strong>} value={<strong className="tabular" data-testid="grand-total">₹ {fmt(totals.grand)}</strong>} big/>
            </div>
          </div>
        </div>
      </div>

      {/* Payment */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Payment Details (optional)</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>Leave empty to save as <strong style={{ color: "#B45309" }}>Draft</strong>. Add a payment method and amount to mark it Balance Pending or Cleared.</p>
        <div className="grid-form">
          <div className="field"><label>Payment Method</label>
            <select data-testid="inv-pay-method" className="select" value={inv.payment_method} onChange={(e) => setInv({ ...inv, payment_method: e.target.value })}>
              <option value="">— None —</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="neft">NEFT</option>
            </select>
          </div>
          <div className="field"><label>Amount Received (₹)</label><input data-testid="inv-amt-recv" className="input num" type="number" step="0.01" min="0" value={inv.amount_received} onChange={(e) => setInv({ ...inv, amount_received: e.target.value })}/></div>
          <div className="field"><label>Balance</label>
            {totals.balance > 0 ? (
              <div className="balance-highlight" data-testid="balance-display">₹ {fmt(totals.balance)} pending</div>
            ) : (
              <div style={{ padding: "9px 12px", background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D", borderRadius: 4, fontWeight: 500 }} data-testid="balance-display">₹ 0.00</div>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        {hasOverstock && <span className="error-msg" style={{ flex: 1 }}>Some line quantities exceed available stock. Please correct them.</span>}
        {!hasOverstock && totals.grand < 0 && <span className="error-msg" style={{ flex: 1 }}>Return amount exceeds the invoice items — reduce the return items or add more items.</span>}
        <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
          <button className="btn btn-secondary" onClick={() => nav("/invoices")}>Cancel</button>
          <button data-testid="submit-invoice-btn" className="btn btn-primary" disabled={!canSubmit || busy} onClick={submit}>{busy ? "Creating..." : "Create Invoice"}</button>
        </div>
      </div>

      {showNewCust && <QuickCustomer onClose={() => setShowNewCust(false)} onCreated={async (id) => { setShowNewCust(false); await loadCustomers(); setInv((p) => ({ ...p, customer_id: id })); }}/>}
    </div>
  );
}

function Row({ label, value, big }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: big ? 16 : 14 }}>
      <span style={{ color: big ? "var(--text)" : "var(--text-muted)" }}>{label}</span>
      <span className="tabular">{value}</span>
    </div>
  );
}

function QuickCustomer({ onClose, onCreated }) {
  const [c, setC] = useState({ name: "", phone: "", address: "", gst_number: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/customers", c);
      onCreated(data.id);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="quick-customer-modal" style={{ maxWidth: 520 }}>
        <div className="modal-h"><h2>Add New Customer</h2><button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button></div>
        <div className="modal-b">
          {err && <div className="error-msg" style={{ marginBottom: 12 }}>{err}</div>}
          <div className="field" style={{ marginBottom: 12 }}><label>Name *</label><input data-testid="qc-name" className="input" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })}/></div>
          <div className="field" style={{ marginBottom: 12 }}><label>Phone</label><input data-testid="qc-phone" className="input" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })}/></div>
          <div className="field" style={{ marginBottom: 12 }}><label>Address</label><textarea data-testid="qc-addr" className="input" rows="3" value={c.address} onChange={(e) => setC({ ...c, address: e.target.value })}/></div>
          <div className="field"><label>GST (optional)</label><input data-testid="qc-gst" className="input" value={c.gst_number} onChange={(e) => setC({ ...c, gst_number: e.target.value })}/></div>
        </div>
        <div className="modal-f">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" data-testid="qc-save" disabled={!c.name || busy} onClick={save}>{busy ? "Saving..." : "Save & Use"}</button>
        </div>
      </div>
    </div>
  );
}
